/**
 * Turning the agent's machine vocabulary into something a person reads.
 *
 * Tool names (`search_work_orders`), argument keys (`workOrderId`) and enum
 * values (`InProgress`) are all contracts with the **model** or the API. They
 * belong exactly as they are in `openwrench-internal-mcp`'s schema, and
 * renaming them upstream would make that schema worse for every other consumer.
 * They are simply not labels: nobody outside engineering should have to work out
 * what `save_file` does, or read `Wo id` mid-conversation. The client is the only
 * layer that owes the reader a human phrase, so the translation lives here.
 *
 * **This is a vocabulary, not a per-tool dictionary.** The MCP server exposes
 * upwards of seventy tools and gains more; a hand-written entry per tool would
 * be stale within a sprint, and a missing entry would fall through to this rule
 * anyway. Getting the *words* right instead means every tool built from those
 * words is right the day it ships. Add a term when one reads wrong — that fixes
 * every identifier containing it at once.
 */

/**
 * Words the generic rule gets wrong. Every entry is shorthand that appears in
 * the OpenWrench domain: verified against identifiers in this repo rather than
 * guessed at, so `nte` (not-to-exceed) and `uom` are here and plausible-sounding
 * acronyms that nothing actually uses are not.
 */
const TERMS: { [word: string]: string } = {
	// Domain shorthand — expanded, because the long form is what people say.
	wo: 'work order',
	wos: 'work orders',
	qty: 'quantity',
	// Engineering jargon that leaked into a tool name.
	upsert: 'create or update',
	// Domain acronyms — kept short, because the long form is not what people say.
	po: 'PO',
	pos: 'POs',
	pm: 'PM',
	pms: 'PMs',
	rfp: 'RFP',
	rfps: 'RFPs',
	nte: 'NTE',
	uom: 'UOM',
	gl: 'GL',
	sla: 'SLA',
	slas: 'SLAs',
	kpi: 'KPI',
	kpis: 'KPIs',
	// Formats and plumbing.
	csv: 'CSV',
	pdf: 'PDF',
	ocr: 'OCR',
	url: 'URL',
	urls: 'URLs',
	api: 'API',
	id: 'ID',
	ids: 'IDs',
};

/**
 * Splits an identifier into words, whatever convention it arrived in: MCP tools
 * are snake_case, argument keys are camelCase, and an acronym run inside a
 * camelCase key (`latestRFPId`) has to break before the next word rather than
 * swallowing its first letter.
 */
const splitIdentifier = (identifier: string): string[] =>
	(identifier || '')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.split(/[_\-\s]+/)
		.filter(Boolean);

/** Applies the vocabulary; a term may expand to more than one word. */
const applyTerms = (words: string[]): string[] => {
	const expanded: string[] = [];
	words.forEach((word) => {
		const replacement = TERMS[word.toLowerCase()];
		if (replacement) {
			replacement.split(' ').forEach((part) => expanded.push(part));
			return;
		}
		// Left alone if it is already an acronym the splitter isolated (`SLA`),
		// lowercased otherwise so `SearchWorkOrders` does not shout.
		expanded.push(word === word.toUpperCase() && word.length > 1 ? word : word.toLowerCase());
	});
	return expanded;
};

/**
 * Names the word-by-word rule cannot see, because they span words. Kept to
 * genuine product names — this is not a back door for per-tool phrasing.
 */
const PHRASES: Array<[RegExp, string]> = [[/\bwrench mode\b/g, 'WrenchMode']];

const sentenceCase = (words: string[]): string => {
	const sentence = PHRASES.reduce(
		(text, [pattern, replacement]) => text.replace(pattern, replacement),
		words.join(' ')
	);
	return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

/** `search_work_orders` -> `Search work orders`, `save_file` -> `Save file`. */
export const humanizeToolName = (name: string): string => {
	const words = applyTerms(splitIdentifier(name));
	return words.length ? sentenceCase(words) : 'a tool';
};

/**
 * `workOrderId` -> `Work order`, `poNumber` -> `PO number`.
 *
 * The trailing `Id` goes because the label sits beside the value and "Work
 * order: #2735653" already reads as an identifier — unless dropping it would
 * leave nothing, as on a bare `id`.
 */
export const humanizeFieldLabel = (key: string): string => {
	const words = applyTerms(splitIdentifier(key));
	const trimmed =
		words.length > 1 && words[words.length - 1].toLowerCase() === 'id'
			? words.slice(0, words.length - 1)
			: words;
	return trimmed.length ? sentenceCase(trimmed) : key;
};

/**
 * Drops a label to lower case for use inside a sentence or as ambient status
 * text — "running get PM schedule", not "Running get PM schedule".
 *
 * Only the first character, and only when the first word is an ordinary one. A
 * blanket `toLowerCase()` flattens everything the vocabulary just got right:
 * `Get PM schedule` came out as "get pm schedule" and `Create RFP` as
 * "create rfp".
 */
const ORDINARY_WORD = /^[A-Z][a-z]*$/;

export const uncapitalize = (text: string): string => {
	const firstWord = (text || '').split(' ')[0] || '';
	return ORDINARY_WORD.test(firstWord) ? text.charAt(0).toLowerCase() + text.slice(1) : text;
};

/**
 * `InProgress` -> `In progress`, `on_hold` -> `On hold`.
 *
 * Values, not labels, so this is deliberately timid: it only fires on something
 * that is unmistakably an enum token — a single short run of letters carrying an
 * internal capital or an underscore. A supplier's name, a work-order title or
 * anything with a space is content and is returned untouched.
 */
const ENUM_TOKEN = /^[A-Za-z][A-Za-z0-9_]*$/;

export const humanizeEnumValue = (value: string): string => {
	const text = (value || '').trim();
	const looksLikeToken =
		text.length > 0 &&
		text.length <= 40 &&
		ENUM_TOKEN.test(text) &&
		(/[a-z][A-Z]/.test(text) || text.indexOf('_') !== -1);
	if (!looksLikeToken) {
		return value;
	}
	return sentenceCase(applyTerms(splitIdentifier(text)));
};
